import React, { useState } from 'react';
import { Shield, QrCode, Key, CheckCircle, AlertCircle } from 'lucide-react';
import { setupMfa, verifyAndEnableMfa } from '../services/api';

export default function MfaSetup({ adminEmail, onEnabled }) {
  const [step, setStep] = useState('setup'); // setup | verify | done
  const [qrCode, setQrCode] = useState('');
  const [manualKey, setManualKey] = useState('');
  const [token, setToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSetup = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await setupMfa(adminEmail);
      if (result.success) {
        setQrCode(result.data.qrCode);
        setManualKey(result.data.manualEntryKey);
        setStep('verify');
      } else {
        setError(result.error || 'Failed to setup MFA');
      }
    } catch (err) {
      // MFA setup error handled silently
      setError('Failed to setup MFA');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (!token.trim()) {
      setError('Enter the 6-digit code');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const result = await verifyAndEnableMfa(adminEmail, token.trim());
      if (result.success) {
        setStep('done');
        setTimeout(() => onEnabled(), 1500);
      } else {
        setError(result.error || 'Invalid code');
      }
    } catch (err) {
      // MFA verification error handled silently
      setError('Invalid code');
    } finally {
      setLoading(false);
    }
  };

  if (step === 'setup') {
    return (
      <div className="bg-white border rounded-lg p-6 max-w-md mx-auto space-y-4">
        <div className="flex items-center gap-2">
          <Shield className="text-primary-900" size={24} />
          <h3 className="text-lg font-semibold text-gray-900">Enable MFA</h3>
        </div>
        <p className="text-sm text-gray-600">
          Add an extra layer of security by requiring a 6-digit code from your authenticator app.
        </p>
        <button
          onClick={handleSetup}
          disabled={loading}
          className="w-full bg-primary-900 text-white px-4 py-2 rounded-lg hover:bg-primary-800 transition disabled:opacity-50"
        >
          {loading ? 'Generating...' : 'Generate QR Code'}
        </button>
        {error && <p className="text-red-600 text-xs">{error}</p>}
      </div>
    );
  }

  if (step === 'verify') {
    return (
      <div className="bg-white border rounded-lg p-6 max-w-md mx-auto space-y-4">
        <div className="flex items-center gap-2">
          <QrCode className="text-primary-900" size={24} />
          <h3 className="text-lg font-semibold text-gray-900">Scan QR Code</h3>
        </div>
        <p className="text-sm text-gray-600">
          Scan the QR code below with Google Authenticator, Authy, or any TOTP app.
        </p>
        {qrCode && <img src={qrCode} alt="MFA QR" className="w-48 h-48 mx-auto border" />}
        <div className="bg-gray-50 rounded p-2">
          <p className="text-xs text-gray-500 mb-1">Can’t scan? Enter this key manually:</p>
          <p className="font-mono text-xs break-all">{manualKey}</p>
        </div>
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Enter 6-digit code</label>
          <input
            type="text"
            maxLength={6}
            value={token}
            onChange={(e) => setToken(e.target.value.replace(/\D/g, ''))}
            placeholder="000000"
            className="w-full border rounded px-3 py-2 text-center text-lg tracking-widest"
          />
        </div>
        <button
          onClick={handleVerify}
          disabled={loading || token.length !== 6}
          className="w-full bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition disabled:opacity-50"
        >
          {loading ? 'Verifying...' : 'Verify & Enable MFA'}
        </button>
        {error && <p className="text-red-600 text-xs">{error}</p>}
      </div>
    );
  }

  return (
    <div className="bg-white border rounded-lg p-6 max-w-md mx-auto space-y-4 text-center">
      <CheckCircle className="text-green-600 mx-auto" size={48} />
      <h3 className="text-lg font-semibold text-gray-900">MFA Enabled</h3>
      <p className="text-sm text-gray-600">Your account now requires an MFA token at login.</p>
    </div>
  );
}
